import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, ChevronDown, Search, FilePlus, ClipboardCheck } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

export function WikiHeader({ searchPages }) {
  const { language, toggleLanguage, t } = useLanguage();
  const { isAuthenticated, isDevMode, user } = useAuth();
  const canContribute = isAuthenticated || isDevMode;
  const isAdmin = isDevMode || user?.role === 'admin';
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const filtered = query.trim()
    ? searchPages.filter((p) => {
        const q = query.toLowerCase();
        return (
          p.title.toLowerCase().includes(q) ||
          p.slug.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q)
        );
      })
    : searchPages;

  return (
    <>
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4">
        <Link to="/wiki" className="flex items-center gap-2 shrink-0">
          <BookOpen className="h-6 w-6 text-blue-600" />
          <span className="text-lg font-semibold text-gray-900">{t('wiki.title')}</span>
        </Link>
        <Link
          to="/"
          className="text-sm text-gray-500 hover:text-gray-800 hidden sm:inline"
        >
          {t('wiki.backToSite')}
        </Link>
        <button
          type="button"
          className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50"
        >
          {t('wiki.allWiki')}
          <ChevronDown className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="flex flex-1 max-w-xl items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100"
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className="truncate">{t('wiki.searchPlaceholder')}</span>
          <kbd className="ml-auto hidden sm:inline rounded border border-gray-200 bg-white px-1.5 text-xs">
            ⌘K
          </kbd>
        </button>
        {canContribute && (
          <Link
            to="/wiki/drafts/new"
            className="hidden md:flex shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
            title={t('wiki.newPage')}
          >
            <FilePlus className="h-4 w-4" />
            <span className="hidden lg:inline">{t('wiki.newPage')}</span>
          </Link>
        )}
        {isAdmin && (
          <Link
            to="/wiki/review"
            className="hidden md:flex shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
            title={t('wiki.reviewQueue')}
          >
            <ClipboardCheck className="h-4 w-4" />
            <span className="hidden lg:inline">{t('wiki.reviewQueue')}</span>
          </Link>
        )}
        <button
          type="button"
          onClick={toggleLanguage}
          className="shrink-0 rounded-md border border-gray-200 px-2.5 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
          title={language === 'zh' ? t('common.langSwitchEn') : t('common.langSwitchZh')}
        >
          {language === 'zh' ? 'EN' : '中文'}
        </button>
      </header>
      {searchOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[15vh]"
          onClick={() => setSearchOpen(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-lg rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={t('wiki.searchDialog')}
          >
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
              <Search className="h-5 w-5 text-gray-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('wiki.searchPlaceholder')}
                className="flex-1 text-sm outline-none text-gray-900"
              />
            </div>
            <ul className="max-h-80 overflow-y-auto py-2">
              {filtered.length === 0 ? (
                <li className="px-4 py-6 text-center text-sm text-gray-500">
                  {t('wiki.noResults')}
                </li>
              ) : (
                filtered.slice(0, 12).map((p) => (
                  <li key={p.slug}>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchOpen(false);
                        setQuery('');
                        navigate(`/wiki/${p.slug}`);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50"
                    >
                      <p className="text-sm font-medium text-gray-900">{p.title}</p>
                      <p className="text-xs text-gray-500 truncate">/wiki/{p.slug}</p>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
